import React from 'react';
import ReactDOM from 'react-dom/client';
import './app/index.css';
import App from './app/App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';

(function () {
  // Constants for storage keys
  const THEME_KEY = 'theme';
  const THEME_MODE_KEY = 'theme-mode';

  // Values for theme mode
  const THEME_MODES = {
    AUTO: 'auto',
    MANUAL: 'manual'
  };

  // Function to get system theme
  const getSystemTheme = () => {
    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
    return prefersDarkScheme.matches ? 'dark' : 'light';
  };

  // Function to get the current theme based on mode
  const getCurrentTheme = () => {
    const themeMode = localStorage.getItem(THEME_MODE_KEY);
    const savedTheme = localStorage.getItem(THEME_KEY);

    // If mode is auto, always use system theme
    if (themeMode === THEME_MODES.AUTO) {
      return getSystemTheme();
    }

    // If mode is manual or not set, use saved theme or default
    if (savedTheme) {
      return savedTheme;
    }

    // Default to system theme if nothing is saved
    const systemTheme = getSystemTheme();
    // Save it as manual to start with
    localStorage.setItem(THEME_KEY, systemTheme);
    localStorage.setItem(THEME_MODE_KEY, THEME_MODES.AUTO);
    return systemTheme;
  };

  // Function to apply theme
  const applyTheme = (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
  };

  // Function to initialize theme
  const initializeTheme = () => {
    // Get current theme mode (default to auto if not set)
    const themeMode = localStorage.getItem(THEME_MODE_KEY) || THEME_MODES.AUTO;

    // Get and apply the theme
    const currentTheme = getCurrentTheme();
    applyTheme(currentTheme);

    // If mode is auto, set up system theme listener
    if (themeMode === THEME_MODES.AUTO) {
      setupSystemThemeListener();
    }

    return currentTheme;
  };

  // Function to setup system theme change listener
  const setupSystemThemeListener = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = (e) => {
      const newTheme = e.matches ? 'dark' : 'light';
      applyTheme(newTheme);

      // Only update localStorage if still in auto mode
      if (localStorage.getItem(THEME_MODE_KEY) === THEME_MODES.AUTO) {
        localStorage.setItem(THEME_KEY, newTheme);
      }
    };

    // Listen for changes
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    // Store cleanup function
    window.__cleanupThemeListener = () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  };

  // Function to switch to manual mode with a specific theme
  const switchToManualMode = (theme) => {
    // Set mode to manual
    localStorage.setItem(THEME_MODE_KEY, THEME_MODES.MANUAL);

    // Save the chosen theme
    localStorage.setItem(THEME_KEY, theme);

    // Apply the theme
    applyTheme(theme);

    // Clean up system theme listener if it exists
    if (window.__cleanupThemeListener) {
      window.__cleanupThemeListener();
      window.__cleanupThemeListener = null;
    }

    return theme;
  };

  // Function to switch to auto mode
  const switchToAutoMode = () => {
    // Set mode to auto
    localStorage.setItem(THEME_MODE_KEY, THEME_MODES.AUTO);

    // Get current system theme
    const systemTheme = getSystemTheme();

    // Save and apply system theme
    localStorage.setItem(THEME_KEY, systemTheme);
    applyTheme(systemTheme);

    // Set up system theme listener
    setupSystemThemeListener();

    return systemTheme;
  };

  // Function to get current theme mode
  const getThemeMode = () => {
    return localStorage.getItem(THEME_MODE_KEY) || THEME_MODES.AUTO;
  };

  initializeTheme();

  window.themeManager = {
    getCurrentTheme,
    getThemeMode,
    switchToAutoMode,
    switchToManualMode,
    getSystemTheme,
    THEME_MODES
  };

})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

reportWebVitals();